import React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const DONUT_COLORS = ["#2563eb", "#10b981", "#ff6b57", "#f59e0b", "#7c3aed"];
const OTHERS_COLOR = "#d1d5db";

function toNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function normalizeName(value) {
  const text = String(value || "").trim();
  if (!text || text === "Not specified") return "Не указано";
  return text;
}

function truncateLabel(value, maxLength = 18) {
  const text = normalizeName(value);
  return text.length > maxLength ? `${text.slice(0, maxLength - 1)}…` : text;
}

function normalizeDynamics(data) {
  return (data || []).map((item) => {
    const high = toNumber(item.high) + toNumber(item.good);
    const medium = toNumber(item.medium);
    const low = toNumber(item.low);
    const total = toNumber(item.total) || high + medium + low;

    return {
      date: item.date,
      high,
      medium,
      low,
      total,
    };
  });
}

function toTopFiveWithOthers(items, nameKey, valueKey) {
  const normalized = (items || [])
    .map((item) => ({
      name: normalizeName(item[nameKey] || item.name),
      value: toNumber(item[valueKey] ?? item.count ?? item.value),
    }))
    .filter((item) => item.value > 0)
    .sort((a, b) => b.value - a.value);

  const topFive = normalized.slice(0, 5);
  const othersValue = normalized.slice(5).reduce((sum, item) => sum + item.value, 0);

  if (othersValue > 0) {
    topFive.push({ name: "Остальные", value: othersValue, isOthers: true });
  }

  return topFive;
}

const QualityDynamicsLegend = () => (
  <div className="mt-4 flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-sm text-slate-600 dark:text-slate-300">
    <div className="flex items-center gap-2">
      <span className="h-3 w-3 rounded-full bg-green-500" />
      <span>Высокое</span>
    </div>
    <div className="flex items-center gap-2">
      <span className="h-3 w-3 rounded-full bg-yellow-400" />
      <span>Среднее</span>
    </div>
    <div className="flex items-center gap-2">
      <span className="h-3 w-3 rounded-full bg-red-500" />
      <span>Низкое</span>
    </div>
  </div>
);

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  const high = payload.find((item) => item.dataKey === "high")?.value || 0;
  const medium = payload.find((item) => item.dataKey === "medium")?.value || 0;
  const low = payload.find((item) => item.dataKey === "low")?.value || 0;

  return (
    <div className="border border-slate-200 bg-white px-4 py-3 text-sm shadow-lg dark:border-slate-700 dark:bg-slate-900">
      <p className="mb-3 text-base font-medium text-slate-950 dark:text-white">{label}</p>
      <p className="text-green-600 dark:text-green-400">Высокое : {high}</p>
      <p className="mt-3 text-yellow-500 dark:text-yellow-300">Среднее : {medium}</p>
      <p className="mt-3 text-red-500 dark:text-red-400">Низкое : {low}</p>
    </div>
  );
};

const DonutTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const item = payload[0]?.payload;
  if (!item) return null;

  return (
    <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 shadow-lg dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
      <p className="font-medium">{item.name}</p>
      <p>{item.value}</p>
    </div>
  );
};

const renderCalloutLabel = ({ cx, cy, midAngle, outerRadius, percent, name }) => {
  if (!percent || percent <= 0.025) return null;

  const RADIAN = Math.PI / 180;
  const labelRadius = outerRadius + 22;
  const x = cx + labelRadius * Math.cos(-midAngle * RADIAN);
  const y = cy + labelRadius * Math.sin(-midAngle * RADIAN);
  const textAnchor = x > cx ? "start" : "end";
  const label = `${truncateLabel(name)} ${Math.round(percent * 100)}%`;

  return (
    <text
      x={x}
      y={y}
      fill="var(--chart-label-color)"
      textAnchor={textAnchor}
      dominantBaseline="central"
      style={{ fontSize: 11, fontWeight: 600 }}
    >
      <title>{`${normalizeName(name)} ${Math.round(percent * 100)}%`}</title>
      {label}
    </text>
  );
};

const DonutChart = ({ data, loading }) => {
  if (loading || data.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-slate-500 dark:text-slate-400">
        {loading ? "Загрузка данных..." : "Недостаточно данных"}
      </div>
    );
  }

  return (
    <div className="h-full w-full overflow-visible p-4 [--chart-label-color:#475569] dark:[--chart-label-color:#cbd5e1]">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart margin={{ top: 28, right: 128, bottom: 28, left: 128 }}>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={54}
            outerRadius={92}
            paddingAngle={2}
            label={renderCalloutLabel}
            labelLine={{ stroke: "var(--chart-label-color)", strokeWidth: 1 }}
          >
            {data.map((entry, index) => (
              <Cell
                key={`${entry.name}-${index}`}
                fill={entry.isOthers ? OTHERS_COLOR : DONUT_COLORS[index % DONUT_COLORS.length]}
              />
            ))}
          </Pie>
          <Tooltip content={<DonutTooltip />} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

const DistributionCard = ({ title, data, loading }) => (
  <section className="rounded-2xl border border-slate-100 bg-white p-6 shadow-md dark:border-slate-800 dark:bg-slate-900">
    <div className="flex min-h-[330px] flex-col gap-4 md:flex-row md:items-center">
      <div className="md:w-[28%]">
        <h2 className="text-2xl font-bold leading-tight text-slate-950 dark:text-white">{title}</h2>
      </div>
      <div className="h-[280px] md:w-[72%]">
        <DonutChart data={data} loading={loading} />
      </div>
    </div>
  </section>
);

const renderTotalLabel = ({ x, y, width, payload }) => {
  if (!payload?.total) return null;

  return (
    <text
      x={x + width / 2}
      y={y - 8}
      textAnchor="middle"
      fill="#0f172a"
      style={{ fontSize: 12, fontWeight: 600 }}
    >
      {payload.total}
    </text>
  );
};

const Charts = ({ leadsQualityDynamics, loading }) => {
  const chartData = normalizeDynamics(leadsQualityDynamics);
  const hasData = chartData.length > 0;

  return (
    <section className="rounded-2xl border border-slate-100 bg-white px-5 py-6 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:px-7">
      <h2 className="mb-6 text-xl font-semibold text-slate-950 dark:text-white">
        Динамика качества лидов по дням
      </h2>

      <div className="h-[360px] w-full">
        {hasData ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 32, right: 12, left: 4, bottom: 10 }} barGap={8}>
              <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12, fill: "#64748b" }}
                axisLine={{ stroke: "#cbd5e1" }}
                tickLine={{ stroke: "#cbd5e1" }}
                interval="preserveStartEnd"
              />
              <YAxis
                tick={{ fontSize: 12, fill: "#64748b" }}
                axisLine={{ stroke: "#cbd5e1" }}
                tickLine={{ stroke: "#cbd5e1" }}
                label={{
                  value: "Количество лидов",
                  angle: -90,
                  position: "insideLeft",
                  offset: 8,
                  style: { textAnchor: "middle", fill: "#334155", fontSize: 12 },
                }}
                domain={[0, (dataMax) => Math.max(100, dataMax + 10)]}
                allowDecimals={false}
              />
              <Tooltip cursor={{ fill: "rgba(148, 163, 184, 0.14)" }} content={<ChartTooltip />} />
              <Bar dataKey="high" name="Высокое" stackId="quality" fill="#22c55e" radius={[0, 0, 6, 6]} />
              <Bar dataKey="medium" name="Среднее" stackId="quality" fill="#facc15" />
              <Bar dataKey="low" name="Низкое" stackId="quality" fill="#ef4444" radius={[6, 6, 0, 0]}>
                <LabelList dataKey="total" content={renderTotalLabel} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-slate-500 dark:text-slate-400">
            {loading ? "Загрузка данных..." : "Нет данных за выбранный период"}
          </div>
        )}
      </div>

      {hasData ? <QualityDynamicsLegend /> : null}
    </section>
  );
};

export const DistributionCharts = ({ aggregations, loading }) => {
  const byModel = toTopFiveWithOthers(aggregations?.by_model || [], "model", "count");
  const byCity = toTopFiveWithOthers(aggregations?.by_city || [], "name", "value");

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <DistributionCard title="Модели автомобилей" data={byModel} loading={loading} />
      <DistributionCard title="Статистика по городам" data={byCity} loading={loading} />
    </div>
  );
};

export const KeyMetricsCard = ({ aggregations }) => {
  const byDate = aggregations?.by_date || aggregations?.leads_quality_dynamics || [];
  const bestDay = byDate.reduce(
    (best, item) => (toNumber(item.total) > toNumber(best.total) ? item : best),
    {}
  );

  const metrics = [
    { label: "Общая конверсия", value: aggregations?.kpi?.conversion || "0%" },
    { label: "Высокий потенциал", value: aggregations?.kpi?.high_potential_pct || "0%" },
    { label: "Максимум заявок за день", value: bestDay.total ?? "-" },
    { label: "Лучший день", value: bestDay.date || "-" },
  ];

  return (
    <section className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <h2 className="mb-6 text-base font-semibold text-slate-800 dark:text-slate-100">Ключевые метрики</h2>
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {metrics.map((metric) => (
          <div key={metric.label}>
            <p className="text-sm text-slate-500 dark:text-slate-400">{metric.label}</p>
            <p className="mt-2 text-3xl font-bold tabular-nums text-slate-950 dark:text-white">{metric.value}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

export default Charts;
